/**
 * Contract for POST /api/v1/imagegen/poster — AI-generated QR-referral /
 * service-showcase poster for a business: logo, business name, an "OUR
 * {servicesLabel}" heading, a QR code with a caption, and a product photo
 * collage, sized for print. (v2 contract — replaces the earlier
 * promotional/offer-flyer shape; see the "poster-contract-v2" spec handed to
 * backend for the full rationale.)
 *
 * Curator-gated (JWT), same family as image-revamp — see ImageRevampObject.ts
 * for the sibling endpoint this mirrors. Takes references to images ALREADY
 * uploaded to Supabase (logo / product photos / a free-form style reference)
 * plus the poster's copy and style intent, and returns a single finished
 * poster inline as base64. Nothing is stored server-side — same as
 * image-revamp, saving the result (if a "save this poster" feature is ever
 * added) would be the caller's responsibility.
 *
 * The response also comes back with two pixel rects — `qrArea` and
 * `reservedZone` — for overlaying a business QR code onto the flat poster
 * client-side. See PosterContent below for what each one means; the backend
 * does NOT render the QR itself or say what it should encode (it's the
 * business's own public page URL, same as ShareProfileModal's QR).
 */

// ── Request ──

/**
 * What each referenced image contributes to the prompt:
 *  - LOGO       the business's own logo/brand mark. REQUIRED — exactly one,
 *               the request should be rejected if missing (the client itself
 *               already blocks generation when no logo is resolvable, but
 *               validate server-side too).
 *  - PRODUCT    real product/interior photos to feature in the background
 *               collage. 0 to 3 of these — genuinely optional (the client's
 *               photo picker is skipped entirely when a business has no
 *               gallery photos yet), not a required minimum.
 *  - REFERENCE  a style/mood reference — NOT rendered verbatim, just informs
 *               look and feel (matches image-revamp's REFERENCE-style
 *               inputs). Exactly one, currently hardcoded client-side.
 */
export type PosterImageRole = "LOGO" | "REFERENCE" | "PRODUCT";

export interface PosterImageInput {
  /** Bucket-relative path of an image ALREADY uploaded — same convention as
   *  ImageRevampSourceImage.objectPath, never raw bytes. */
  objectPath: string;
  role: PosterImageRole;
}

/**
 * The poster's actual copy — v2 shape, built around the service-showcase
 * template (see the module doc comment). The earlier promotional fields
 * (headline/offer/validity/callToAction) are gone; they belonged to a
 * different poster type this client no longer generates.
 */
export interface PosterContentInput {
  /** Business display name, rendered under the logo. Required — always
   *  taken from the business's own profile, never typed by the user in this
   *  modal. */
  businessName: string;
  /** The single dynamic word completing "OUR {word}" in the poster's
   *  heading (e.g. "SERVICES", "MENU", "DESIGNS"). Optional — the client
   *  defaults this to "SERVICES" if left blank rather than sending an empty
   *  string. Kept short (~20 chars) so it fits the heading layout. */
  servicesLabel?: string;
  /** Caption line rendered directly below the QR code. Optional — if
   *  omitted, backend should render `"Scan here to explore our
   *  {servicesLabel}"` as the default. The client always resolves this
   *  itself before sending (see NineFindsAssetModal's handleGenerate), but
   *  the field stays optional here so the same fallback rule holds for any
   *  other caller. Kept short (~60 chars) to avoid wrapping under the QR. */
  qrCaption?: string;
}

export interface PosterStyle {
  /** Hex color, e.g. "#7B2D26". */
  baseBackgroundColor?: string;
  /** Accent colors — the example payload sends exactly one; unclear if more
   *  than one is meaningful or how they'd be applied. */
  palette?: string[];
  /** Free string in every other part of this codebase (see BusinessObject's
   *  own `listingType: string`) — not a fixed union here either. */
  listingType?: string;
  /** Free text — no confirmed server-side cap for this endpoint (unlike
   *  image-revamp's documented 800). Not enforced client-side until known. */
  instructions?: string;
  // `purpose` REMOVED in v2 — this endpoint now generates a single template
  // (the service-showcase poster), so a purpose/type switch isn't needed.
}

export interface PosterRequest {
  businessId: string;
  images: PosterImageInput[];
  content: PosterContentInput;
  style?: PosterStyle;
}

// ── Response ──

export interface PosterImage {
  /** Always JPEG per the sibling image-revamp contract's convention — render
   *  with `data:image/jpeg;base64,` + base64. Full-resolution, not a
   *  screen-sized preview — but NOT a fixed size: one confirmed sample came
   *  back 2480x3508 (A4 @ 300 DPI), another 1024x1448 (same ~1:√2 aspect
   *  ratio, far smaller). Always draw at the image's own width/height,
   *  never upscale a smaller canvas into it or assume A4 dimensions. */
  base64: string;
  width: number;
  height: number;
}

/**
 * A pixel rect in the SAME coordinate space as `image.width`/`image.height`
 * — no percentage math needed, draw straight into these coordinates at the
 * poster's native resolution.
 */
export interface PosterRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PosterContent {
  /** Written to a server-side audit row — quote it in bug reports, same as
   *  image-revamp's generationId. */
  generationId: string;
  image: PosterImage;
  /** The AI's own suggested QR rect. Client-side compositing
   *  (PosterCompositionHelper.compositePosterWithQr) now sizes the drawn QR
   *  to `reservedZone` instead (minus a small cosmetic inset) — per product
   *  feedback that drawing at just `qrArea` left a visible gap inside the
   *  white card. Kept as a fallback rect for when `reservedZone` is ever
   *  absent — don't remove it from the contract. */
  qrArea: PosterRect;
  /**
   * The zone the AI was told to keep visually clear of headline/logo/product
   * art — always `qrArea` inflated by an equal margin on every side, but the
   * margin itself is NOT a fixed pixel value: one sample showed 40px (at
   * 2480x3508), another 24px (at 1024x1448) — derive it per-response as
   * `(reservedZone.width - qrArea.width) / 2` rather than hardcoding either
   * number. This is also what the QR is now actually sized to draw into
   * (see qrArea's doc above) — the white card this reserves should be
   * treated as the QR's real target size, not just a backdrop behind a
   * smaller code.
   */
  reservedZone: PosterRect;
  aiGenerated: boolean;
  promptVersion: string;
  createdAt: string;
}

export interface PosterGeneralProcessingStatus {
  processingStatus: "OK" | "ERROR";
  processingDetails: string;
}

/** Same envelope shape as ImageRevampErrorInformation. */
export interface PosterErrorInformation {
  errorCode: string;
  errorDetail: string;
  additionalDetails: Record<string, any> | null;
}

export interface PosterResponse {
  /** null on success, populated (never empty) on failure. */
  errorInformation: PosterErrorInformation[] | null;
  generalProcessingStatus: PosterGeneralProcessingStatus;
  /** null on failure. Exactly one entry on success — no batching, same as
   *  image-revamp's `contents`. */
  contents: PosterContent[] | null;
}

// ── Error codes ──

/**
 * NOT CONFIRMED against a spec for this endpoint — mirrored from
 * IMAGE_REVAMP_ERROR_CODES on the assumption that both endpoints share the
 * same /api/v1/imagegen auth/validation middleware (including image-revamp's
 * own BULK_CREATE_* prefix quirk on UNAUTHENTICATED/NOT_CURATOR). Verify with
 * backend and correct this table once the real codes for /poster are known —
 * until then, an unrecognized code still falls back to the server's own
 * errorDetail via posterErrorCopy below, so nothing breaks if this guess is
 * wrong, it just shows terser copy.
 */
export const POSTER_ERROR_CODES = {
  VALIDATION_FAILED: "AI_IMG_VALIDATION_FAILED",
  UNSUPPORTED_FORMAT: "AI_IMG_UNSUPPORTED_FORMAT",
  MODERATION_BLOCKED: "AI_IMG_MODERATION_BLOCKED",
  UNAUTHENTICATED: "BULK_CREATE_UNAUTHENTICATED",
  NOT_CURATOR: "BULK_CREATE_NOT_CURATOR",
  QUOTA_EXCEEDED: "AI_IMG_QUOTA_EXCEEDED",
  BUSY: "AI_IMG_BUSY",
  PROVIDER_FAILED: "AI_IMG_PROVIDER_FAILED",
  BUDGET_EXCEEDED: "AI_IMG_BUDGET_EXCEEDED",
} as const;

export const POSTER_ERROR_COPY: Record<
  string,
  { message: string; retryable: boolean }
> = {
  [POSTER_ERROR_CODES.VALIDATION_FAILED]: {
    message: "That request was missing something the AI tool needs. Please try again.",
    retryable: true,
  },
  [POSTER_ERROR_CODES.UNSUPPORTED_FORMAT]: {
    message: "One of those photos can't be used — convert it to JPEG or PNG and try again.",
    retryable: true,
  },
  [POSTER_ERROR_CODES.MODERATION_BLOCKED]: {
    message: "This request couldn't be processed. Try different photos or copy.",
    retryable: false,
  },
  [POSTER_ERROR_CODES.UNAUTHENTICATED]: {
    message: "Your session has expired. Please log in again.",
    retryable: false,
  },
  [POSTER_ERROR_CODES.NOT_CURATOR]: {
    message: "Only curators can use this tool.",
    retryable: false,
  },
  [POSTER_ERROR_CODES.QUOTA_EXCEEDED]: {
    message: "Daily AI image limit reached. Try again tomorrow.",
    retryable: false,
  },
  [POSTER_ERROR_CODES.BUSY]: {
    message: "The AI tool is busy right now. Please try again in a moment.",
    retryable: true,
  },
  [POSTER_ERROR_CODES.PROVIDER_FAILED]: {
    message: "Something went wrong generating that poster. Please try again.",
    retryable: true,
  },
  [POSTER_ERROR_CODES.BUDGET_EXCEEDED]: {
    message: "That took too long to generate. Please try again.",
    retryable: true,
  },
};

/** Falls back to the server's own errorDetail, then to generic copy — an
 *  errorCode this build has never heard of (including if the guess above
 *  turns out wrong) still reads as something. */
export function posterErrorCopy(
  errorCode: string | null | undefined,
  errorDetail?: string | null
): { message: string; retryable: boolean } {
  if (errorCode && POSTER_ERROR_COPY[errorCode]) {
    return POSTER_ERROR_COPY[errorCode];
  }
  return {
    message: errorDetail || "Couldn't generate that poster. Try again.",
    retryable: true,
  };
}
